export class Store {
  constructor({ data, isHidden, index }) {
    if (!data || typeof isHidden === 'undefined' || typeof index === 'undefined') {
      throw new Error('There are not enough data to build Store');
    }

    this.data = data;
    this.node = null;
    this.isHidden = isHidden;
    this.index = index;
  }

  /**
   * Builds the URL for the location address.
   * @returns {string} The Google Maps URL for the location address.
   */
  #setLocationAddressUrl() {
    return `https://maps.google.com/?q=Windsor,${encodeURIComponent(
      `${this.data.address.address_line_1},${this.data.address.address_line_2},${this.data.address.city},${this.data.address.state},${this.data.address.zip_code}`,
    )}`;
  }

  /**
   * Builds the address HTML.
   * @returns {string} The HTML string for the address.
   */
  #buildAddress() {
    return `
      <p>${this.data.address.address_line_1}</p>
      <p>${this.data.address.address_line_2}</p>
      <p>${this.data.address.city}, ${this.data.address.state} ${this.data.address.zip_code}</p>
    `;
  }

  /**
   * Builds the quantity text.
   * @param {Number | string} quantity
   * @returns {string} The quantity text.
   */
  #buildQuantity(quantity) {
    if (quantity > 0) {
      return `${quantity} in stock`;
    } else {
      return 'Sold out';
    }
  }

  /**
   * Builds the distance text.
   * @param {Number | string} distance
   * @returns {string} The distance text.
   */
  #buildDistance(distance) {
    return `${distance.toFixed(2)} miles`;
  }

  /**
   * Builds the location name element.
   * @returns {HTMLElement} The location name element.
   */
  #buildLocationName() {
    const locationName = document.createElement('h4');
    locationName.classList.add('pdp-c-find-store__location-name');
    locationName.textContent = this.data.store_name;
    return locationName;
  }

  /**
   * Builds the location hours element.
   * @returns {HTMLElement} The location hours element.
   */
  #buildLocationHours() {
    // Build details and summary elements
    const locationDetailsPod = document.createElement('details');
    locationDetailsPod.classList.add('pdp-c-find-store__location-details');
    const locationHoursLabel = document.createElement('summary');
    locationHoursLabel.textContent = 'Location details';
    locationDetailsPod.appendChild(locationHoursLabel);

    // Build content
    const locationDetailsContent = document.createElement('div');
    locationDetailsContent.classList.add('pdp-c-find-store__location-full-details');
    // Build an interactive location address link
    const locationAddress = document.createElement('a');
    locationAddress.classList.add('pdp-c-find-store__location-address');
    locationAddress.href = this.#setLocationAddressUrl();
    locationAddress.innerHTML = this.#buildAddress();
    locationDetailsContent.appendChild(locationAddress);
    // Build location hours
    const hoursList = document.createElement('ul');
    hoursList.classList.add('pdp-c-find-store__location-hours');
    this.data.business_hours.forEach((date) => {
      const dateItem = document.createElement('li');
      dateItem.innerHTML = `
        <p>${date.day}</p>
        <p>${date.hours}</p>
      `;
      hoursList.appendChild(dateItem);
    });
    locationDetailsContent.appendChild(hoursList);

    // Build hide location button
    const hideLocationBtn = document.createElement('a');
    hideLocationBtn.textContent = 'Hide Location Details';
    hideLocationBtn.classList.add('pdp-c-find-store__location-hide-btn');
    hideLocationBtn.addEventListener('click', () => locationDetailsPod.removeAttribute('open'));
    locationDetailsContent.appendChild(hideLocationBtn);

    // Add content to details
    locationDetailsPod.appendChild(locationDetailsContent);

    // Add Tracking Events
    locationDetailsPod.addEventListener('toggle', this.#handleOpenLocationDetailsClick.bind(this));
    locationAddress.addEventListener('click', this.#handleLocationAddressClick.bind(this));
    return locationDetailsPod;
  }

  /** Handle click on details */
  #handleOpenLocationDetailsClick(event) {
    if (event.target.open) this.#handleStoreDetailsTrackingClick();
  }

  /** Handle click on anchor element */
  #handleLocationAddressClick() {
    this.#handleStoreAddressTrackingClick(this.data.store_name);
  }

  /**
   * Builds the location availability element.
   * @returns {HTMLElement} The location availability element.
   */
  #buildLocationAvailability() {
    const locationAvailability = document.createElement('p');
    locationAvailability.setAttribute(
      'aria-describedby',
      `findstore-tooltip-${this.data.store_name.toLowerCase().replace(/\s/g, '-')}`,
    );
    locationAvailability.classList.add('pdp-c-find-store__location-availability');
    locationAvailability.classList.toggle('pdp-c-find-store__location-availability--available', this.data.quantity > 0);
    locationAvailability.classList.toggle(
      'pdp-c-find-store__location-availability--sold-out',
      this.data.quantity === 0,
    );
    locationAvailability.textContent = this.#buildQuantity(this.data.quantity);
    return locationAvailability;
  }

  /**
   * Set a tooltip on available items
   * @returns {HTMLElement} - tool-tip element
   */
  #buildTooltip() {
    const tooltip = document.createElement('tool-tip');
    tooltip.role = 'tooltip';
    tooltip.id = `findstore-tooltip-${this.data.store_name.toLowerCase().replace(/\s/g, '-')}`;
    tooltip.classList.add('pdp-c-tooltip');
    tooltip.setAttribute('tooltip-position', 'left');
    tooltip.innerHTML = `*We're unable to put store items on hold. Availability in store isn't guaranteed.`;
    return tooltip;
  }

  /**
   * Builds the location distance element.
   * @returns {HTMLElement} The location distance element.
   */
  #buildLocationDistance() {
    const locationDistance = document.createElement('p');
    locationDistance.classList.add('pdp-c-find-store__location-distance');
    locationDistance.textContent = this.#buildDistance(this.data.distance);
    return locationDistance;
  }

  /**
   * Renders the location item element.
   * @returns {HTMLElement} The location item element.
   */
  render() {
    // Build a Location item pod
    const locationWrapper = document.createElement('div');
    locationWrapper.setAttribute('data-index', this.index);
    locationWrapper.classList.add('pdp-c-find-store__location-item');
    if (this.isHidden) locationWrapper.classList.add('pdp-c-find-store__location-item--hidden');

    // Build left side: Location name and hours
    const leftSide = document.createElement('div');
    leftSide.classList.add('pdp-c-find-store__location-column');

    leftSide.appendChild(this.#buildLocationName());
    leftSide.appendChild(this.#buildLocationHours());
    locationWrapper.appendChild(leftSide);

    // Build right side: Availability and Distance
    const rightSide = document.createElement('div');
    rightSide.classList.add('pdp-c-find-store__location-column', 'pdp-c-find-store__location-column--right');

    this.data.quantity !== 0 && rightSide.appendChild(this.#buildTooltip());
    rightSide.appendChild(this.#buildLocationAvailability());
    rightSide.appendChild(this.#buildLocationDistance());
    locationWrapper.appendChild(rightSide);
    this.node = locationWrapper;

    return locationWrapper;
  }

  /**
   * Push GTM tracking when a location details is opened
   * @returns {void} - nothing
   */
  #handleStoreDetailsTrackingClick() {
    if (typeof window === 'undefined' && !window.dataLayer) return;
    window.dataLayer.push({
      event: 'pdp_local_store_availability_storedetails_click',
    });
  }

  /**
   * Push GTM tracking when a location is watched or rendered
   * @returns {void} - nothing
   */
  handleStoreImpression() {
    window.dataLayer.push({
      event: 'store_address_impression',
      store_location: this.data.store_name,
      product_page: window.location.href,
    });
  }

  /**
   * Push GTM tracking when user clicked any location address link
   * @param {string} storeName - store location name
   * @returns
   */
  #handleStoreAddressTrackingClick(storeName) {
    if (typeof window === 'undefined' && !window.dataLayer) return;
    window.dataLayer.push({
      event: 'store_address_click',
      store_location: storeName,
      product_page: window.location.href,
    });
  }
}
